/**
 * Composant Plateau de jeu
 * Chaque joueur voit son pion en bas de l'écran
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useGame } from '../hooks/useGame';
import { BOARD_SIZE, getValidMoves } from '@pathblock/core';
import type { LastAction } from '@pathblock/core';

export const Board: React.FC = () => {
  const {
    gameState,
    playerId,
    isMyTurn,
    placingWall,
    wallOrientation,
    wallPreview,
    handleCellClick,
    handleCellHover,
    handlePlacementClick,
  } = useGame();

  // Joueur 1 voit le plateau inversé (son pion en bas)
  const shouldFlip = playerId === 1;

  // Calculer les mouvements valides (en coordonnées réelles)
  const validMoves = useMemo(() => {
    if (!gameState || !playerId || !isMyTurn || placingWall || gameState.phase !== 'playing') {
      return new Set<string>();
    }
    const moves = getValidMoves(gameState, playerId);
    return new Set(moves.map((m) => `${m.row},${m.col}`));
  }, [gameState, playerId, isMyTurn, placingWall]);

  if (!gameState) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Chargement...</div>
      </div>
    );
  }

  // Convertir coordonnées d'affichage vers coordonnées réelles
  const toReal = (displayRow: number, displayCol: number) => {
    if (shouldFlip) {
      return {
        row: BOARD_SIZE - 1 - displayRow,
        col: BOARD_SIZE - 1 - displayCol,
      };
    }
    return { row: displayRow, col: displayCol };
  };

  // Convertir coordonnées réelles vers coordonnées d'affichage
  const toDisplay = (realRow: number, realCol: number) => {
    if (shouldFlip) {
      return {
        row: BOARD_SIZE - 1 - realRow,
        col: BOARD_SIZE - 1 - realCol,
      };
    }
    return { row: realRow, col: realCol };
  };

  const p1Pos = gameState.players[0].position;
  const p2Pos = gameState.players[1].position;
  const p1Display = toDisplay(p1Pos.row, p1Pos.col);
  const p2Display = toDisplay(p2Pos.row, p2Pos.col);

  // Cellules liées à la dernière action (en coordonnées d'affichage)
  const lastAction: LastAction | null = gameState.lastAction ?? null;
  const lastFromDisplay = lastAction?.type === 'move' && lastAction.from
    ? toDisplay(lastAction.from.row, lastAction.from.col)
    : null;
  const lastToDisplay = lastAction?.type === 'move' && lastAction.to
    ? toDisplay(lastAction.to.row, lastAction.to.col)
    : null;

  const isPlacement = gameState.phase === 'placement';
  const myPlayer = playerId ? gameState.players[playerId - 1] : null;
  // En affichage, la ligne de départ est toujours en bas (row = BOARD_SIZE-1)
  // et la ligne cible est toujours en haut (row = 0)
  const myStartDisplayRow = BOARD_SIZE - 1;
  const myTargetDisplayRow = 0;
  // Case cible du joueur courant (pendant la partie, colonne choisie)
  const myTargetDisplay = myPlayer
    ? toDisplay(myPlayer.targetRow, myPlayer.targetCol)
    : null;

  // Hover pendant le placement : colonne survolée dans l'espace d'affichage
  const [hoverDisplayCol, setHoverDisplayCol] = useState<number | null>(null);

  // Taille de cellule dynamique selon la largeur de l'écran
  const [cellSize, setCellSize] = useState(() =>
    Math.min(48, Math.floor((window.innerWidth - 48) / 9))
  );

  useEffect(() => {
    const handleResize = () => {
      setCellSize(Math.min(48, Math.floor((window.innerWidth - 48) / 9)));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trouver le mur à afficher en haut de cette cellule (coordonnées d'affichage)
  const getTopWall = (displayRow: number, displayCol: number) => {
    if (displayRow === 0) return null;
    const real = toReal(displayRow, displayCol);

    return gameState.walls.find((w) => {
      if (w.orientation !== 'horizontal') return false;
      if (shouldFlip) {
        // Mur horizontal { row, col } bloque entre rows row-1 et row, spans cols col et col+1.
        // En vue inversée : ancre display à (BOARD_SIZE - row, BOARD_SIZE - 2 - col)
        const wallDisplayRow = BOARD_SIZE - w.row;
        const wallDisplayCol = BOARD_SIZE - 2 - w.col;
        return wallDisplayRow === displayRow && wallDisplayCol === displayCol;
      } else {
        return w.row === real.row && w.col === real.col;
      }
    });
  };

  // Trouver le mur à afficher à gauche de cette cellule
  const getLeftWall = (displayRow: number, displayCol: number) => {
    if (displayCol === 0) return null;
    const real = toReal(displayRow, displayCol);

    return gameState.walls.find((w) => {
      if (w.orientation !== 'vertical') return false;
      if (shouldFlip) {
        // Mur vertical { row, col } bloque entre cols col-1 et col, spans rows row et row+1.
        // En vue inversée : ancre display à (BOARD_SIZE - 2 - row, BOARD_SIZE - col)
        const wallDisplayRow = BOARD_SIZE - 2 - w.row;
        const wallDisplayCol = BOARD_SIZE - w.col;
        return wallDisplayCol === displayCol && wallDisplayRow === displayRow;
      } else {
        return w.col === real.col && w.row === real.row;
      }
    });
  };

  // Prévisualisation du mur
  const getTopPreview = (displayRow: number, displayCol: number) => {
    if (!wallPreview || wallPreview.orientation !== 'horizontal' || displayRow === 0) return false;
    const real = toReal(displayRow, displayCol);
    if (shouldFlip) {
      const previewDisplayRow = BOARD_SIZE - wallPreview.row;
      const previewDisplayCol = BOARD_SIZE - 2 - wallPreview.col;
      return previewDisplayRow === displayRow && previewDisplayCol === displayCol;
    }
    return wallPreview.row === real.row && wallPreview.col === real.col;
  };

  const getLeftPreview = (displayRow: number, displayCol: number) => {
    if (!wallPreview || wallPreview.orientation !== 'vertical' || displayCol === 0) return false;
    const real = toReal(displayRow, displayCol);
    if (shouldFlip) {
      const previewDisplayRow = BOARD_SIZE - 2 - wallPreview.row;
      const previewDisplayCol = BOARD_SIZE - wallPreview.col;
      return previewDisplayCol === displayCol && previewDisplayRow === displayRow;
    }
    return wallPreview.col === real.col && wallPreview.row === real.row;
  };

  // Taille d'une cellule en pixels (dynamique selon l'écran)
  const CELL_SIZE = cellSize;
  // Un mur couvre exactement 2 cases
  const WALL_LENGTH = CELL_SIZE * 2;

  return (
    <div className="bg-gray-800 p-4 rounded-xl shadow-2xl">
      {/* Indicateur en haut */}
      <div className="mb-2 text-center text-xs font-medium">
        {isPlacement ? (
          <span className="text-teal-400">↑ Votre ligne d'arrivée</span>
        ) : (
          <span className="text-teal-400">
            ↑ Votre objectif
          </span>
        )}
      </div>

      <div
        className="grid gap-0 border-2 border-gray-600 rounded overflow-hidden"
        style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, ${CELL_SIZE}px)`, touchAction: 'none' }}
        onTouchMove={(e) => e.preventDefault()}
      >
        {Array.from({ length: BOARD_SIZE }).map((_, displayRow) =>
          Array.from({ length: BOARD_SIZE }).map((_, displayCol) => {
            const real = toReal(displayRow, displayCol);
            const isValid = validMoves.has(`${real.row},${real.col}`);
            const hasP1 = p1Display.row === displayRow && p1Display.col === displayCol;
            const hasP2 = p2Display.row === displayRow && p2Display.col === displayCol;

            const topWall = getTopWall(displayRow, displayCol);
            const leftWall = getLeftWall(displayRow, displayCol);
            const topPreview = getTopPreview(displayRow, displayCol);
            const leftPreview = getLeftPreview(displayRow, displayCol);

            // --- Placement phase ---
            const isMyConfirmed = isPlacement && !!myPlayer?.positionConfirmed;
            const isPlacementClickable = isPlacement && displayRow === myStartDisplayRow && !isMyConfirmed;
            const isMyChosenCell = isMyConfirmed
              && myPlayer!.position.row === real.row && myPlayer!.position.col === real.col;
            // Cellule cible prévisualisée en haut quand on survole (avant confirmation)
            const isTargetPreview = isPlacement && !isMyConfirmed
              && displayRow === myTargetDisplayRow && hoverDisplayCol === displayCol;
            // Cellule de départ prévisualisée dans la ligne de départ au hover
            const isStartHover = isPlacementClickable && hoverDisplayCol === displayCol;
            // Toute la ligne d'arrivée (en haut) surlignée légèrement
            const isArrivalRow = isPlacement && displayRow === myTargetDisplayRow;

            // --- Partie en cours ---
            const isTargetCell = !isPlacement && !!myTargetDisplay
              && myTargetDisplay.row === displayRow && myTargetDisplay.col === displayCol;

            // Dernière action: case de départ et d'arrivée
            const isLastFrom = lastFromDisplay
              && lastFromDisplay.row === displayRow && lastFromDisplay.col === displayCol;
            const isLastTo = lastToDisplay
              && lastToDisplay.row === displayRow && lastToDisplay.col === displayCol;

            // Couleur de fond calculée une seule fois pour éviter les conflits Tailwind
            let cellBg = 'bg-gray-700';
            if (isPlacement) {
              if (isMyChosenCell)       cellBg = 'bg-blue-600';
              else if (isTargetPreview) cellBg = 'bg-teal-700';
              else if (isStartHover)    cellBg = 'bg-blue-800';
              else if (isPlacementClickable) cellBg = 'bg-gray-500 cursor-pointer';
              else if (isArrivalRow)    cellBg = 'bg-teal-900/60';
            } else {
              if (isTargetCell)                              cellBg = 'bg-teal-700 animate-goal-glow';
              else if (isValid && isMyTurn && !placingWall) cellBg = 'bg-green-600 cursor-pointer';
              else if (isLastTo)                             cellBg = 'bg-yellow-700/60';
              else if (isLastFrom)                           cellBg = 'bg-gray-600';
            }

            return (
              <div
                key={`${displayRow}-${displayCol}`}
                className="relative"
                style={{ width: CELL_SIZE, height: CELL_SIZE }}
              >
                {/* Mur horizontal en haut (2 cases de large) */}
                {topWall && (() => {
                  const isLastWall = lastAction?.type === 'wall' && lastAction.wall
                    && lastAction.wall.row === topWall.row
                    && lastAction.wall.col === topWall.col
                    && lastAction.wall.orientation === topWall.orientation;
                  return (
                    <div
                      className={`absolute z-20 shadow-lg transition-colors duration-300 ${isLastWall ? 'bg-amber-400' : 'bg-orange-500'}`}
                      style={{
                        height: '8px',
                        width: `${WALL_LENGTH}px`,
                        top: '-4px',
                        left: '0px',
                        borderRadius: '4px',
                      }}
                    />
                  );
                })()}

                {/* Mur vertical à gauche (2 cases de haut) */}
                {leftWall && (() => {
                  const isLastWall = lastAction?.type === 'wall' && lastAction.wall
                    && lastAction.wall.row === leftWall.row
                    && lastAction.wall.col === leftWall.col
                    && lastAction.wall.orientation === leftWall.orientation;
                  return (
                    <div
                      className={`absolute z-20 shadow-lg transition-colors duration-300 ${isLastWall ? 'bg-amber-400' : 'bg-orange-500'}`}
                      style={{
                        width: '8px',
                        height: `${WALL_LENGTH}px`,
                        left: '-4px',
                        top: '0px',
                        borderRadius: '4px',
                      }}
                    />
                  );
                })()}

                {/* Prévisualisation mur horizontal */}
                {topPreview && !topWall && (
                  <div
                    className="absolute bg-yellow-400 z-10"
                    style={{
                      height: '8px',
                      width: `${WALL_LENGTH}px`,
                      top: '-4px',
                      left: '0px',
                      borderRadius: '4px',
                      opacity: 0.7,
                    }}
                  />
                )}

                {/* Prévisualisation mur vertical */}
                {leftPreview && !leftWall && (
                  <div
                    className="absolute bg-yellow-400 z-10"
                    style={{
                      width: '8px',
                      height: `${WALL_LENGTH}px`,
                      left: '-4px',
                      top: '0px',
                      borderRadius: '4px',
                      opacity: 0.7,
                    }}
                  />
                )}

                {/* Cellule */}
                <div
                  className={`
                    w-full h-full flex items-center justify-center
                    border border-gray-600
                    transition-colors duration-100
                    ${cellBg}
                    ${isTargetCell ? 'ring-2 ring-teal-400 ring-inset' : ''}
                    ${!isPlacement && isMyTurn && placingWall ? 'cursor-crosshair hover:bg-gray-600' : ''}
                  `}
                  onClick={() => {
                    if (isPlacement) {
                      if (isPlacementClickable) handlePlacementClick(real.col);
                    } else {
                      handleCellClick(real.row, real.col);
                    }
                  }}
                  onMouseEnter={() => {
                    if (isPlacement) {
                      if (displayRow === myStartDisplayRow) setHoverDisplayCol(displayCol);
                    } else {
                      handleCellHover(real.row, real.col);
                    }
                  }}
                  onMouseLeave={() => {
                    if (isPlacement && displayRow === myStartDisplayRow) {
                      setHoverDisplayCol(null);
                    }
                  }}
                  onTouchStart={() => {
                    if (isPlacement) {
                      if (displayRow === myStartDisplayRow) setHoverDisplayCol(displayCol);
                    } else {
                      handleCellHover(real.row, real.col);
                    }
                  }}
                >
                  {/* Pion joueur 1 (rouge) */}
                  {hasP1 && (
                    <div
                      className={`
                        w-9 h-9 rounded-full bg-red-500 border-2 border-white shadow-lg
                        flex items-center justify-center text-white font-bold text-sm
                        transition-all duration-200
                        ${gameState.currentPlayer === 1
                          ? 'ring-4 ring-red-300/50 animate-pawn-active'
                          : 'opacity-80'}
                      `}
                    >
                      1
                    </div>
                  )}

                  {/* Pion joueur 2 (bleu) */}
                  {hasP2 && (
                    <div
                      className={`
                        w-9 h-9 rounded-full bg-blue-500 border-2 border-white shadow-lg
                        flex items-center justify-center text-white font-bold text-sm
                        transition-all duration-200
                        ${gameState.currentPlayer === 2
                          ? 'ring-4 ring-blue-300/50 animate-pawn-active'
                          : 'opacity-80'}
                      `}
                    >
                      2
                    </div>
                  )}

                  {/* Prévisualisation pion pendant le placement */}
                  {isStartHover && !hasP1 && !hasP2 && (
                    <div className={`w-9 h-9 rounded-full border-2 border-white/40 opacity-50
                      ${playerId === 1 ? 'bg-red-500' : 'bg-blue-500'}`}
                    />
                  )}

                  {/* Point mouvement valide */}
                  {isValid && isMyTurn && !placingWall && !hasP1 && !hasP2 && (
                    <div className="w-4 h-4 rounded-full bg-white/60" />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Indicateur en bas */}
      <div className="mt-2 text-center text-xs font-medium">
        {isPlacement ? (
          myPlayer?.positionConfirmed ? (
            <span className="text-green-400">✓ Position confirmée — En attente de l'adversaire...</span>
          ) : (
            <span className="text-yellow-400">↓ Choisissez votre case de départ</span>
          )
        ) : (
          <span className="text-gray-500">
            ↓ Camp adverse
          </span>
        )}
      </div>

      {/* Mode actuel */}
      {placingWall && (
        <div className="mt-3 p-2 bg-orange-500/20 rounded text-center text-orange-300 text-sm">
          🧱 Mode Mur: {wallOrientation === 'horizontal' ? '━━ Horizontal' : '┃┃ Vertical'}
        </div>
      )}
    </div>
  );
};
